import { WorldPoint, RoadSegment, DummyDog, DummyWalkerArchetype, DummyWalkerNPC } from '../domain/types.ts';
import { snapToRoad } from '../logic/roadNavigation.ts';

export type ChibiNpcArchetype = DummyWalkerArchetype;

export interface SoundWave {
  id: string;
  x: number;
  y: number;
  radius: number;
  maxRadius: number; // Mặc định 25m
  speed: number;     // Tốc độ lan truyền sóng âm (m/s)
  lifeTimer: number; // Thời gian tồn tại (ms)
  sourceName: string;
  color: string;
}

export interface ChibiNPC extends DummyWalkerNPC {
  fov: number;         // Góc nhìn (radians)
  visionRange: number; // Tầm nhìn (mét)
  hasDog: boolean;
  dog?: DummyDog;
  hasFlashlight?: boolean;
  hasPhone?: boolean;
  isAlarming?: boolean;
  alarmCooldownTimer?: number;
  isRushingToScene?: boolean;
  rushTarget?: WorldPoint | null;
  waypoints?: WorldPoint[];
  targetMove?: WorldPoint | null;
}

export const ARCHETYPE_CONFIG: Record<
  ChibiNpcArchetype,
  {
    icon: string;
    bodyColor: string;
    fov: number;
    visionRange: number;
    baseSpeed: number;
    speechColor: string;
  }
> = {
  ba_tam: {
    icon: '👵',
    bodyColor: '#e91e63',
    fov: Math.PI * 0.55, // 99 độ
    visionRange: 18,
    baseSpeed: 2.8,
    speechColor: '#ff1744',
  },
  bac_huu_tri: {
    icon: '👴',
    bodyColor: '#795548',
    fov: Math.PI * 0.45,
    visionRange: 16,
    baseSpeed: 2.2,
    speechColor: '#d50000',
  },
  gen_z: {
    icon: '📱',
    bodyColor: '#9c27b0',
    fov: Math.PI * 0.6,
    visionRange: 20,
    baseSpeed: 3.5,
    speechColor: '#aa00ff',
  },
  dan_phong: {
    icon: '👮‍♂️',
    bodyColor: '#1565c0',
    fov: Math.PI * 0.65,
    visionRange: 22,
    baseSpeed: 4.5,
    speechColor: '#2979ff',
  },
  nguoi_tho_o: {
    icon: '🎧',
    bodyColor: '#607d8b',
    fov: Math.PI * 0.35,
    visionRange: 12,
    baseSpeed: 3.0,
    speechColor: '#78909c',
  },
  altruist: {
    icon: '👩‍💼',
    bodyColor: '#ec407a',
    fov: Math.PI * 0.5,
    visionRange: 16,
    baseSpeed: 3.2,
    speechColor: '#00e676',
  },
  defector: {
    icon: '🏃‍♂️',
    bodyColor: '#ff5722',
    fov: Math.PI * 0.45,
    visionRange: 15,
    baseSpeed: 4.0,
    speechColor: '#ff9100',
  },
  tit_for_tat: {
    icon: '🧔',
    bodyColor: '#0288d1',
    fov: Math.PI * 0.5,
    visionRange: 16,
    baseSpeed: 3.4,
    speechColor: '#40c4ff',
  },
};

/**
 * Sinh danh sách NPC Chibi vỉa hè dựa trên mạng lưới đường thực tế
 */
export function createChibiPedestrians(roads: RoadSegment[]): ChibiNPC[] {
  if (!roads || roads.length === 0) return [];

  const npcs: ChibiNPC[] = [];
  const archetypes: ChibiNpcArchetype[] = [
    'ba_tam',
    'gen_z',
    'dan_phong',
    'bac_huu_tri',
    'nguoi_tho_o',
    'tit_for_tat',
  ];

  const names: Record<ChibiNpcArchetype, string> = {
    ba_tam: 'Bà Bảy Quán Cơm (Thánh Mẫu)',
    bac_huu_tri: 'Cụ Hưng (Cán bộ hưu trí)',
    gen_z: 'Bé Ly TikToker (100k Tim)',
    dan_phong: 'Anh Tuấn (Dân phòng ngõ)',
    nguoi_tho_o: 'Quân Đi Bụi (Đồng phạm)',
    altruist: 'Bảo Ngọc (Thánh Mẫu)',
    defector: 'Tiến Chuột (Kẻ lén lút)',
    tit_for_tat: 'Hùng Còi (Tit-for-Tat)',
  };

  // Chỉ lấy tối đa 5-6 tuyến đường gần khu vực dạo phố để tạo NPC, tránh quá tải khi load OSM lớn
  const sortedRoads = [...roads]
    .filter((r) => r.points.length >= 2)
    .sort((a, b) => {
      const minA = Math.min(...a.points.map((p) => Math.hypot(p.x, p.y)));
      const minB = Math.min(...b.points.map((p) => Math.hypot(p.x, p.y)));
      return minA - minB;
    })
    .slice(0, 6);

  sortedRoads.forEach((road, roadIdx) => {
    if (road.points.length < 2) return;
    const arch = archetypes[roadIdx % archetypes.length]!;
    const cfg = ARCHETYPE_CONFIG[arch];

    // Lấy một điểm trên đường làm điểm xuất phát
    const startPt = road.points[0]!;
    const nextPt = road.points[1]!;
    const angle = Math.atan2(nextPt.y - startPt.y, nextPt.x - startPt.x);

    const patrolRoute: WorldPoint[] = [...road.points];
    if (road.points.length >= 2) {
      // Vòng tròn đi ngược lại
      patrolRoute.push(...[...road.points].reverse());
    }

    const npc: ChibiNPC = {
      id: `chibi-npc-${roadIdx + 1}`,
      name: names[arch] || `Cư dân ngõ ${roadIdx + 1}`,
      archetype: arch,
      x: startPt.x,
      y: startPt.y,
      facingAngle: angle,
      speed: cfg.baseSpeed,
      fov: cfg.fov,
      visionRange: cfg.visionRange,
      patrolRoute,
      currentRouteIndex: 0,
      waypoints: patrolRoute.slice(1),
      targetMove: patrolRoute[1] || null,
      hasDog: arch === 'tit_for_tat' || arch === 'ba_tam',
      hasFlashlight: arch === 'ba_tam' || arch === 'dan_phong',
      hasPhone: arch === 'gen_z',
      hasWitnessedPlayerDefect: false,
      isSeekingRevenge: false,
      inChickenGameWithPlayer: false,
      isCleaning: false,
      cleanTimer: 0,
      isFleeing: false,
      fleeTimer: 0,
    };

    if (npc.hasDog) {
      npc.dog = {
        x: startPt.x - Math.cos(angle) * 1.5,
        y: startPt.y - Math.sin(angle) * 1.5,
        facingAngle: angle,
        bowels: 35,
        isPooping: false,
        poopTimer: 0,
        breadcrumbs: [],
      };
    }

    npcs.push(npc);
  });

  return npcs;
}

/**
 * Xử lý phát ngôn và phản ứng tri hô khi NPC phát hiện hành vi bậy bạ
 */
// =========================================================================
// NGÂN HÀNG CÂU THOẠI ĐA DẠNG (DIALOGUE POOLS) — HỘI THOẠI VỈA HÈ HÀ NỘI
// =========================================================================
export const NPC_DIALOGUE_POOLS: Record<
  ChibiNpcArchetype,
  {
    defect: string[];
    pooping: string[];
    cooperate: string[];
    alarm_heard?: string[];
    emotes: string[];
  }
> = {
  ba_tam: {
    defect: [
      'Ối làng nước ơi!',
      'Trời đất quỷ thần ơi, con chó nhà ai!',
      'Thấy hết rồi nha, đừng hòng chuồn!',
      'Bớ người ta ỉa bậy!',
      'Tôi nhớ mặt rồi đấy nhé!',
      'Này này, tính vắt chân lên cổ chạy à!',
    ],
    pooping: [
      'Nhìn coi nó xả kìa, có mang túi không đấy?',
      'Xả ngay ngõ thế kia hả trời!',
      'Liệu mà dọn sạch đấy con ơi!',
      'Gớm, dắt chó đi ị canh đúng cửa nhà người ta!',
    ],
    cooperate: [
      'Cũng biết ý tứ dọn sạch đấy, tốt!',
      'Thế mới là công dân văn minh chứ!',
      'Dọn sạch vậy xóm làng mới quý!',
    ],
    alarm_heard: [
      'Ối làng nước ơi! Ra xem đứa xả bậy này!',
      'Đâu? Ai tri hô đấy, ra xem ngay!',
      'Lại đứa nào vô ý thức à!',
      'Bác tổ trưởng ơi ra xem này!',
    ],
    emotes: ['❗', '👀', '🚨', '❓'],
  },
  bac_huu_tri: {
    defect: [
      'Đứng lại! Bác ghi sổ ngay!',
      'Mất nết! Tổ trưởng đâu rồi!',
      'Thanh niên bây giờ ý thức kém quá!',
      'Lại đây nộp phạt!',
      'Không thể chấp nhận được cái thói này!',
    ],
    pooping: [
      'Hôi hám cả ngõ, liệu mà dọn sạch!',
      'Cẩn thận không bẩn giày người đi bộ!',
      'Ý thức để đâu mà đứng đấy ngó!',
      'Chó phóng uế bừa bãi là phạt nặng đấy!',
    ],
    cooperate: [
      'Thế có phải văn minh không!',
      'Đáng khen cho tinh thần tự giác!',
      'Tốt lắm, ngõ phố cần những người như cháu!',
    ],
    alarm_heard: [
      'Ai làm mất trật tự tổ dân phố đấy?',
      'Bác tổ trưởng ra đây lập biên bản ngay!',
      'Mất trật tự quá, trật tự trị an đâu rồi!',
    ],
    emotes: ['❗', '💢', '❓', '✍️'],
  },
  gen_z: {
    defect: [
      'Úi chà, lên tóp tóp 100k tim!',
      'Say hi camera đi bro!',
      'Góc quay này xịn xò nè!',
      'Ố dè, phốt căng!',
      'Cười ẻ, tính lén lút chuồn kìa!',
      'Quả này lên xu hướng chắc luôn!',
    ],
    pooping: [
      'Cún cưng cute thế, đang livestream nha!',
      'Check-in cảnh cún xả bãi mìn vỉa hè!',
      'Căng cực căng cực bro ơi!',
      'Dáng đi vệ sinh chuẩn 10 điểm!',
    ],
    cooperate: [
      'Check-in công dân gương mẫu 10 điểm!',
      '10 điểm không có nhưng!',
      'Uy tín luôn bạn iu ơi!',
      'Dễ thương quá, thả tim nha!',
    ],
    alarm_heard: [
      'Ủa có biến căng hả anh em?',
      'Bật live hóng biến gấp!',
      'Hóng hóng, ai quậy đục nước vậy?',
    ],
    emotes: ['📸', '📱', '✨', '👀'],
  },
  dan_phong: {
    defect: [
      'ĐỨNG LẠI! DỌN NGAY KHÔNG LẬP BIÊN BẢN!',
      'Đồng chí kia dừng lại! Vi phạm điều lệ khu phố!',
      'Có camera ghi hình rồi, không thoát được đâu!',
      'Đề nghị giữ nguyên hiện trường, không được bỏ chạy!',
    ],
    pooping: [
      'Chuẩn bị túi dọn ngay nhé đồng chí!',
      'Quan sát giữ vệ sinh ngõ phố!',
      'Nhắc nhở đồng chí phải gom chất thải cún!',
    ],
    cooperate: [
      'Cảm ơn đồng chí đã giữ vệ sinh chung!',
      'Chấp hành tốt quy định tổ dân phố!',
      'Rất hoan nghênh tinh thần tự giác của đồng chí!',
    ],
    alarm_heard: [
      'Có tiếng tri hô, ra xem ngay!',
      'Đâu? Ai làm bậy đâu?',
      'Đề nghị giữ nguyên hiện trường!',
      'Khu vực này có biến à?',
      'Lực lượng dân phòng tới đây!',
    ],
    emotes: ['🚨', '👮', '❗', '⚡'],
  },
  nguoi_tho_o: {
    defect: [
      'Thúi quá ba...',
      'Chuồn lẹ đi cha nội.',
      'Ai rảnh đâu dọn...',
      'Oạch, đạp trúng mìn!',
      'Né lẹ kẻo bị vạ lây...',
    ],
    pooping: [
      'Eo ơi bốc mùi quá...',
      'Chó mót rồi kìa...',
      'Tự nhiên đứng lại ngửi ngửi chi zậy...',
    ],
    cooperate: [
      'Dọn làm chi cho mệt xác...',
      'Chăm chỉ thế...',
      'Ôi bẩn tay chết...',
    ],
    alarm_heard: [
      'Ủa cái gì ồn ào vậy?',
      'Biến căng rồi, tẩu vi thượng sách!',
      'Hét to thế điếc cả tai...',
    ],
    emotes: ['💨', '🎧', '🙄', '❓'],
  },
  altruist: {
    defect: [
      'Ối dồi ôi, thanh niên mặt mũi sáng sủa mà tính chuồn hả con vợ!',
      'Chụp nét căng rồi nhé! Chuẩn bị tinh thần lên Group Cư Dân!',
      'Đứng lại ngay! Văn minh lịch sự để đâu mà để chó ỉa xong chuồn?',
      'Tôi đã quay lại video rồi, tự giác quay lại dọn ngay!',
    ],
    pooping: [
      'Cún xả phân nhớ dọn sạch nhé bạn ơi!',
      'Giữ gìn môi trường xanh sạch đẹp nhé!',
    ],
    cooperate: [
      'Cũng biết điều đấy, tặng bạn 2 điểm công dân gương mẫu!',
      'Nuôi chó có ý thức như này có phải xóm làng yên vui không!',
      'Tốt lắm bạn ơi! Xã hội cần nhiều người như bạn!',
    ],
    alarm_heard: [
      'Mọi người ơi ra xem kẻ vi phạm!',
      'Cùng nhau giữ gìn văn minh khu phố nào!',
    ],
    emotes: ['❗', '📢', '🌸'],
  },
  defector: {
    defect: [
      'Chuồn lẹ không bà tổ trưởng ra bây giờ!',
      'Chó xả xong rồi, vắt chân lên cổ mà chạy thôi!',
      'Ai ỉa đâu không biết, tao vô tội nhé!',
      'Phóng nhanh kẻo camera quét trúng!',
    ],
    pooping: [
      'Tranh thủ ngó quanh xem có ai nhìn không...',
      'Xả nhanh rồi té thôi cún!',
    ],
    cooperate: [
      'Đần thế con vợ, dọn làm gì cho bẩn tay!',
      'Khổ thân, làm nô tì dọn phân cho chó giữa đường thế kia!',
      'Ơ kìa, cứ chuồn như tao có phải khỏe re không!',
    ],
    alarm_heard: [
      'Chết bà, chuồn gấp!',
      'Có biến, té thôi con vợ!',
    ],
    emotes: ['🏃', '💨', '👀'],
  },
  tit_for_tat: {
    defect: [
      'Tao ghim mày rồi đấy, đợi đấy con vợ!',
      'Dám chuồn trước mặt tao à? Đợi chó tao mót ruột nhé!',
      'Mày ỉa bậy được thì tao cũng biết đường sang nhà mày!',
      'Ghi nhớ biển số mặt mày rồi, quả báo sắp tới!',
    ],
    pooping: [
      'Mày nhìn tao tao nhìn lại, sòng phẳng!',
      'Coi chừng xả bậy tao trả đũa!',
    ],
    cooperate: [
      'Mày dọn thì tao cũng dọn, hòa bình là trên hết!',
      'Sống biết điều cho đời thanh thản!',
      'Người văn minh, duyệt!',
    ],
    alarm_heard: [
      'Ai đụng đến ai thế? Để tao ra xem!',
      'Được lắm, để xem mặt mũi là ai!',
    ],
    emotes: ['😠', '⚔️', '👀'],
  },
};

export function getRandomNpcDialogue(
  archetype: DummyWalkerArchetype,
  action: 'defect' | 'pooping' | 'cooperate' | 'alarm_heard'
): string {
  const pool = NPC_DIALOGUE_POOLS[archetype] || NPC_DIALOGUE_POOLS.ba_tam;
  const list = pool[action] || pool.defect;
  return list[Math.floor(Math.random() * list.length)]!;
}

export function getRandomNpcEmote(archetype: DummyWalkerArchetype): string {
  const pool = NPC_DIALOGUE_POOLS[archetype] || NPC_DIALOGUE_POOLS.ba_tam;
  return pool.emotes[Math.floor(Math.random() * pool.emotes.length)] || '❗';
}

/**
 * Xử lý phát ngôn và phản ứng tri hô khi NPC phát hiện hành vi bậy bạ
 */
export function handleNpcWitnessAction(
  npc: DummyWalkerNPC,
  action: 'pooping' | 'defect' | 'cooperate',
  targetPos: WorldPoint
): {
  soundWave?: SoundWave;
  speechText: string;
  speechColor: string;
  paranoiaSurge: number;
  reputationPenalty: number;
  disgraceMultiplierBonus: number;
} {
  const cfg = ARCHETYPE_CONFIG[npc.archetype];
  const speechText = getRandomNpcDialogue(npc.archetype, action);
  let paranoiaSurge = 5;
  let reputationPenalty = 0;
  let disgraceMultiplierBonus = 0;
  let shouldAlarm = false;

  if (action === 'defect') {
    switch (npc.archetype) {
      case 'ba_tam':
        paranoiaSurge = 25;
        disgraceMultiplierBonus = 1.0;
        shouldAlarm = true;
        break;
      case 'bac_huu_tri':
        paranoiaSurge = 20;
        reputationPenalty = 8;
        shouldAlarm = true;
        break;
      case 'gen_z':
        paranoiaSurge = 15;
        disgraceMultiplierBonus = 2.0; // Tăng cực mạnh hệ số nhục nhã bóc phốt
        shouldAlarm = true;
        break;
      case 'dan_phong':
        paranoiaSurge = 30;
        reputationPenalty = 15;
        shouldAlarm = true;
        break;
      case 'nguoi_tho_o':
        paranoiaSurge = 2;
        break;
      case 'altruist':
        paranoiaSurge = 18;
        shouldAlarm = true;
        break;
      case 'defector':
        paranoiaSurge = 0;
        break;
      case 'tit_for_tat':
        paranoiaSurge = 12;
        break;
    }
  }

  let soundWave: SoundWave | undefined = undefined;
  if (shouldAlarm) {
    soundWave = {
      id: `soundwave-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`,
      x: npc.x,
      y: npc.y,
      radius: 1,
      maxRadius: 25, // 25 mét
      speed: 22,    // 22 m/s
      lifeTimer: 1800,
      sourceName: npc.name,
      color: cfg.speechColor,
    };
  }

  return {
    soundWave,
    speechText,
    speechColor: cfg.speechColor,
    paranoiaSurge,
    reputationPenalty,
    disgraceMultiplierBonus,
  };
}
